import { FormBuilder } from '@/components/formBuilder';
import {
  Button,
  Flex,
  // Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Box,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';

type JobActionId = 'createJob' | 'updateJob' | 'cloneJob';

interface JobLike {
  id?: string | number;
  [key: string]: unknown;
}

export interface JobCreateEditModalConfig {
  id?: JobActionId;
  label?: string;
  description?: string;
  modalActionText?: string;
  loadingText?: string;
  renderType?: 'notForm';
  initialValues?: Record<string, unknown>;
  /** Performs the request and resolves with the job. Rejects on failure. */
  apiCall: (_values: any) => Promise<JobLike | undefined>;
  /** Runs after a successful call, before the modal closes. */
  onSuccess?: (_job: JobLike | undefined) => void;
  /** Create only — stay put instead of navigating to the new job. */
  skipRedirect?: boolean;
  /**
   * Override the toast copy when the action does more than its id implies.
   * A function sees the resolved job and may return nothing to skip the toast,
   * for callers whose `apiCall` did part of the work and reported it itself.
   */
  successMessage?: string | ((_job: JobLike | undefined) => string | undefined);
  failureMessage?: string;
  /** Callers also hang their own menu metadata here — `icon`, `testID`, `onClick`. */
  [key: string]: any;
}

interface JobCreateEditModalProps {
  /** Chakra useDisclosure handle driving the modal. */
  modalActions?: { isOpen?: boolean; onClose?: () => void; [key: string]: any };
  /** Mode, prefilled job data and callbacks, supplied by the caller. */
  config?: Partial<JobCreateEditModalConfig>;
}

const SUCCESS_COPY: Record<JobActionId, string> = {
  createJob: 'Job created successfully',
  updateJob: 'Job updated successfully',
  cloneJob: 'Job cloned successfully',
};

const FAILURE_COPY: Record<JobActionId, string> = {
  createJob: 'Error in creating job',
  updateJob: 'Error in updating job',
  cloneJob: 'Error in cloning job',
};

/** Where to land once the job exists. Updates stay on the page. */
const redirectFor = (
  action: JobActionId | undefined,
  id: string | number | undefined
) => {
  if (!id) return undefined;
  if (action === 'createJob') return `/jobs/${id}`;
  if (action === 'cloneJob') return `/jobs/${id}?isClonedJob=true`;
  return undefined;
};

export const JobCreateEditModal = ({
  modalActions = {},
  config = {},
}: JobCreateEditModalProps) => {
  // `getAtsJobCreate` comes from an untyped slice, so the shape is asserted here
  // rather than inferred as `{}`.
  const { currentJob = {} } = (useSelector(getAtsJobCreate) ?? {}) as {
    currentJob?: { data?: { id?: string | number; name?: string } };
  };
  const { data: { name: jobName, id: jobId } = {} } = currentJob;
  const { isOpen, onClose } = modalActions;
  const {
    id: configId,
    label,
    renderType,
    // icon,
    description,
    modalActionText,
    apiCall,
    loadingText,
    initialValues,
    onSuccess,
    skipRedirect = false,
    successMessage,
    failureMessage,
  } = config;

  const formRef = useRef<{ handleSubmit: () => void } | null>(null);
  const toast = useShowToast();
  const history = useHistory();

  const { data: ats_jobs = [] } = useGetAllJobsListQuery({
    params: {
      scope: 'viewable',
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const renderHeader = () => {
    return (
      <Flex gap="1rem" alignItems="center">
        {/* todo: enable if needed in future
        <Icon as={icon} color="ats-primary" w="2em" h="2em" /> */}
        <Flex flexDir="column" gap="4px">
          <KText size="md-medium" color="secondary-900">
            {label}
          </KText>
          <KText size="sm-medium" color="secondary-700">
            {description}
          </KText>
        </Flex>
      </Flex>
    );
  };

  /**
   * The result is awaited here rather than watched for in Redux.
   *
   * Callers used to dispatch a thunk that wrote into the `atsJobs` slice, and
   * this modal ran three effects keyed on `createJob.data.id`, `updateJob.data.id`
   * and `errors` to toast, redirect and close. That left the outcome one render
   * behind the request and made every caller responsible for dispatching
   * `resetJobState` first — otherwise the previous run's id was still sitting in
   * the store and the effect fired again the moment the modal opened.
   */
  const runAction = async (values: unknown) => {
    if (!apiCall) return;
    setIsSubmitting(true);
    try {
      const job = await apiCall(values);
      onSuccess?.(job);

      const success =
        typeof successMessage === 'function'
          ? successMessage(job)
          : (successMessage ?? (configId && SUCCESS_COPY[configId]));
      if (success) toast({ title: success });

      onClose?.();

      const redirect = skipRedirect
        ? undefined
        : redirectFor(configId, job?.id);
      if (redirect) history.push(redirect);
    } catch (err) {
      toast({
        title:
          failureMessage ??
          (configId ? FAILURE_COPY[configId] : 'Something went wrong'),
        description: err instanceof Error ? err.message : undefined,
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBody = () => {
    const createFormConfig = [
      {
        name: 'name',
        label: 'Internal job name',
        type: 'input',
        isRequired: true,
        maxLength: 255,
      },
      {
        name: 'confidential',
        label: 'Is confidential',
        type: 'checkbox',
        labelInfoText: `Confidential jobs are visible only to hiring team and users with the “Manage all confidential jobs” permission`,
      },
    ];
    const cloneFormConfig = [
      {
        name: 'cloneJobId',
        label: 'Internal Job',
        type: 'select',
        isRequired: true,
        options: ats_jobs,
        labelKey: 'name',
        valueKey: 'id',
      },
    ];

    if (renderType === 'notForm') {
      return (
        <Flex p="0 1em 1em 1.6em">
          <KText fontSize="sm">
            Cloning this job
            <KText as="span" size="sm-semi-bold">
              {' '}
              {jobName}{' '}
            </KText>
            will create a duplicate without any candidates
          </KText>
        </Flex>
      );
    }

    return (
      <Box m=".5em 1.5em">
        <FormBuilder
          config={configId === 'cloneJob' ? cloneFormConfig : createFormConfig}
          onSubmit={runAction}
          initialValues={{ confidential: false, ...initialValues }}
          formRef={formRef}
        />
      </Box>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
        isCentered
        trapFocus={configId !== 'cloneJob'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb="0">{renderHeader()}</ModalHeader>
          <ModalBody p="0">{renderBody()}</ModalBody>
          <ModalFooter pt="0">
            <Button
              variant="secondary"
              mr={3}
              data-test-id="secondary-cta-button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              loadingText={loadingText}
              variant="primary"
              data-test-id="primary-cta-button"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.handleSubmit();
                } else if (renderType === 'notForm') {
                  runAction(jobId);
                }
              }}
            >
              {modalActionText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return <>{renderModal()}</>;
};
